import autoBind from 'auto-bind';
import { Hymn, vocalFileExtensions, Voice, voicesForVocalFiles, VoiceValues } from '../../../common/src/model.ts';
import { encodeMusicURI, hymnalsDir, vocalsDir } from '../../../common/src/paths.ts';
import { NetworkError } from '../util/network.ts';
import { generateFileName, generatePathFor } from '../util/path.ts';
import AudioBufferLite from './audio_buffer_lite.ts';
import { DualAudioElementInterface } from './dual_audio_element.tsx';
import { createAudioContext } from './instrument.ts';
import { MP3AudioPlayer } from './mp3_audio_player.ts';

export class VocalsPlayer extends MP3AudioPlayer {
  audioContext = createAudioContext();
  initialTime: string = '0:00';
  urlPrefix: string | undefined;
  mp3URL: string | undefined;
  silentNode: OscillatorNode | undefined;
  songReady: () => void;
  onNetworkError: (networkError: NetworkError) => void;
  onVoiceNamesChange?: (voiceNames: Voice[]) => void;
  buffers: { [voice in Voice]?: AudioBuffer } = {};

  constructor({
    dualAudioElement,
    hymn,
    songReady,
    onDurationChange,
    onNetworkError,
    onVoiceNamesChange,
    endOfFile,
    getVoiceValues,
    updateLocationSlider,
  }: {
    dualAudioElement: DualAudioElementInterface;
    hymn: Hymn;
    songReady: () => void;
    onDurationChange?: (duration: number | undefined) => void;
    onNetworkError: (networkError: NetworkError) => void;
    onVoiceNamesChange?: (voiceNames: Voice[]) => void;
    endOfFile: () => void;
    getVoiceValues: () => VoiceValues;
    updateLocationSlider?: () => void;
  }) {
    super({ dualAudioElement, endOfFile, onDurationChange, updateLocationSlider, getVoiceValues });
    autoBind(this);
    const { extensions, basePath, hymnal, vocals } = hymn;
    const extension = vocalFileExtensions.find((extension) => extensions?.includes(extension));
    if (extension) {
      this.mp3URL = generatePathFor(basePath || `/${hymnalsDir}`, hymnal, `${generateFileName(hymn)}.${extension}`);
    } else {
      if (!vocals) {
        console.error('No .vocals for hymn with vocals:', hymnal, hymn);
      }
      const vocalsPath = `/${vocalsDir}`;
      const vocalsSongName = vocals ?? '';
      this.urlPrefix = generatePathFor(vocalsPath, hymnal, vocalsSongName) + '/' + encodeMusicURI(vocalsSongName);
    }

    this.songReady = songReady;
    this.onNetworkError = onNetworkError;
    this.onVoiceNamesChange = onVoiceNamesChange;

    void this._loadMP3Vocals();
  }

  get fileTempo(): number | undefined {
    return undefined;
  }

  private _getMP3URL(voice: Voice) {
    const suffixMap: { [voice in Voice]?: string } = {
      [Voice.Soprano]: ' Soprano.mp3',
      [Voice.Alto]: ' Alto.mp3',
      [Voice.Tenor]: ' Tenor.mp3',
      [Voice.Bass]: ' Bass.mp3',
      [Voice.Piano]: ' Piano.mp3',
    };
    if (!suffixMap[voice]) {
      console.error('Unsupported voice', voice);
    }
    return this.urlPrefix + encodeMusicURI(suffixMap[voice] || ' UNKNOWN_VOICE.mp3');
  }

  private async _loadMP3Vocals() {
    const promises: Promise<void>[] = [];
    for (const voice of this.voices) {
      promises.push(this._downloadMP3(voice));
    }
    await Promise.all(promises);

    this.initialTime = '0:00';
    this.onVoiceNamesChange?.(this.voices);
    await this.refreshMP3();
    this.songReady();
  }

  get voices(): Voice[] {
    return this.mp3URL ? [Voice.Vocal] : voicesForVocalFiles;
  }

  protected async refreshMP3() {
    const primaryBuffer = this.buffers[this.voices[0]];
    if (!primaryBuffer) {
      console.error(`No soprano buffer found for vocals player (not generating MP3): ${this.urlPrefix}`);
      return;
    }
    const numberOfChannels = Math.max(...Object.values(this.buffers).map((buffer) => buffer.numberOfChannels));
    const { sampleRate, duration } = primaryBuffer;
    const buffer = new AudioBufferLite(numberOfChannels, sampleRate, duration);
    const voiceValues = this.getVoiceValues();
    const currentIteration = this.abortPreviousMP3Refresh();
    const shouldAbort = () => this.shouldAbortMP3Refresh(currentIteration);
    for (const voice of this.voices) {
      if (!this.buffers[voice]) {
        console.error(`No ${voice} buffer found for vocals player (skipping voice): ${this.urlPrefix}`);
        continue;
      }
      buffer.recordNote(this.buffers[voice], 0, duration, voiceValues[voice] ?? 0.5, {
        ignoreChannelMismatch: true,
      });
      if (shouldAbort()) {
        console.log(`aborted vocals _refreshMP3`);
        return;
      }
    }
    await buffer.normalize({ normalizationTarget: Math.max(...Object.values(voiceValues)) });
    const audioBlob = buffer.exportAsWavBlob();
    if (shouldAbort()) {
      console.log(`aborted vocals _refreshMP3`);
      return;
    }
    await this.audioElement.setSrc(audioBlob);
  }

  private async _downloadMP3(voice: Voice): Promise<void> {
    const response = await fetch(this.mp3URL ?? this._getMP3URL(voice));
    if (!response.ok) {
      this.onNetworkError(NetworkError.MP3DownloadFailed);
      return;
    }
    const buffer = await response.arrayBuffer();
    this.buffers[voice] = await this.audioContext.decodeAudioData(buffer);
  }

  playFirstNote(_voice: Voice, _startTick?: number) {
    console.log(`vocals player does not yet support playing first note`);
  }

  get ticksPerBeat(): number | undefined {
    return undefined;
  }
}
